// src/handover/handoverStyles.js

/* ===================== Design System ===================== */
export const COLORS = {
  primary: '#2563eb',
  primaryDark: '#1e40af',
  primaryLight: '#eff6ff',
  success: '#10b981',
  successLight: '#d1fae5',
  danger: '#ef4444',
  dangerLight: '#fee2e2',
  warning: '#f59e0b',
  warningLight: '#fef3c7',
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },
};

/* ===================== Responsive Utilities ===================== */
export const getResponsiveValue = (mobile, tablet, desktop) => {
  // Usage: const value = getResponsiveValue(12, 16, 20)
  // Then use with media queries
  return { mobile, tablet, desktop };
};

export const mediaQuery = {
  mobile: '(max-width: 640px)',
  tablet: '(min-width: 641px) and (max-width: 1024px)',
  desktop: '(min-width: 1025px)',
};

/* ===================== Card & Container Styles ===================== */
export const cardStyle = (extra = {}) => ({
  borderRadius: 16,
  border: `1px solid ${COLORS.gray[200]}`,
  background: '#ffffff',
  padding: 24,
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07), 0 1px 3px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.2s ease',
  ...extra,
});

export const panelStyle = {
  borderRadius: 12,
  border: `1px solid ${COLORS.gray[200]}`,
  background: COLORS.gray[50],
  padding: 16,
  marginBottom: 16,
};

/* ===================== Typography ===================== */
export const labelStyle = {
  display: 'block',
  fontSize: 14,
  color: COLORS.gray[700],
  fontWeight: 600,
  marginBottom: 6,
  letterSpacing: '0.3px',
};

export const sectionTitleStyle = {
  margin: '18px 0 12px',
  fontSize: 16,
  fontWeight: 700,
  color: COLORS.gray[900],
};

export const helperTextStyle = {
  fontSize: 12,
  color: COLORS.gray[600],
  marginTop: 4,
  lineHeight: 1.4,
};

/* ===================== Input Styles ===================== */
export const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: `1px solid ${COLORS.gray[300]}`,
  fontSize: 14,
  outline: 'none',
  background: '#ffffff',
  boxSizing: 'border-box',
  transition: 'all 0.2s ease',
  color: COLORS.gray[900],
  fontFamily: 'inherit',
};

export const textareaStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: `1px solid ${COLORS.gray[300]}`,
  fontSize: 14,
  outline: 'none',
  background: '#ffffff',
  boxSizing: 'border-box',
  transition: 'all 0.2s ease',
  color: COLORS.gray[900],
  minHeight: 100,
  resize: 'vertical',
  fontFamily: 'inherit',
};

export const selectStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: `1px solid ${COLORS.gray[300]}`,
  fontSize: 14,
  outline: 'none',
  background: '#ffffff',
  boxSizing: 'border-box',
  transition: 'all 0.2s ease',
  color: COLORS.gray[900],
  cursor: 'pointer',
  fontFamily: 'inherit',
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
  backgroundPosition: 'right 0.5rem center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '1.5em 1.5em',
  paddingRight: '2.5rem',
};

/* ===================== Grid Layouts ===================== */
export const twoColGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 14,
};

export const threeColGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 14,
};

export const responsiveGrid = (cols = 2) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fit, minmax(260px, 1fr))`,
  gap: 14,
});

/* ===================== Status Badge ===================== */
export const statusBadge = (status) => {
  let bg = COLORS.gray[100];
  let color = COLORS.gray[700];

  if (status === 'COMPLETED' || status === 'success') {
    bg = COLORS.successLight;
    color = '#065f46';
  } else if (status === 'PENDING' || status === 'warning') {
    bg = COLORS.warningLight;
    color = '#92400e';
  } else if (status === 'ERROR' || status === 'error') {
    bg = COLORS.dangerLight;
    color = '#7f1d1d';
  }

  return {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    background: bg,
    color,
  };
};

/* ===================== Mobile Utilities ===================== */
export const mobileResponsivePadding = (isMobile) => ({
  padding: isMobile ? 16 : 24,
});

export const mobileResponsiveGap = (isMobile) => ({
  gap: isMobile ? 12 : 16,
});

/* ===================== Button Styles ===================== */
export const buttonBaseStyle = {
  padding: '10px 16px',
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 600,
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};
